import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from './utils';

export type CardVariant = 'surface' | 'inset' | 'glass' | 'plain';
export type CardRadius = 'sm' | 'md' | 'lg' | 'xl' | '2xl';
export type CardShadow = 'none' | 'card' | 'soft' | 'pop';

export type CardProps = HTMLAttributes<HTMLDivElement> & {
  variant?: CardVariant;
  radius?: CardRadius;
  shadow?: CardShadow;
  /** Default body padding scale. Pass `none` for full-bleed. */
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  children?: ReactNode;
};

const VARIANT_CLS: Record<CardVariant, string> = {
  surface: 'surface-card',
  inset: 'surface-inset',
  glass: 'glass-panel',
  plain: 'bg-transparent',
};

const RADIUS_CLS: Record<CardRadius, string> = {
  sm: 'rounded-md',
  md: 'rounded-xl',
  lg: 'rounded-2xl',
  xl: 'rounded-[20px]',
  '2xl': 'rounded-[28px]',
};

const SHADOW_CLS: Record<CardShadow, string> = {
  none: '',
  card: 'shadow-card',
  soft: 'shadow-soft',
  pop: 'shadow-pop',
};

type CardPadding = NonNullable<CardProps['padding']>;
const PADDING_CLS: Record<CardPadding, string> = {
  none: '',
  sm: 'p-3',
  md: 'p-4 md:p-5',
  lg: 'p-5 md:p-6',
  xl: 'p-6 md:p-10',
};

/**
 * Card — the application's universal surface container.
 * Defaults to a white, hairline-bordered, `shadow-card` surface with `rounded-xl` radius.
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  {
    variant = 'surface',
    radius = 'lg',
    shadow = 'card',
    padding = 'none',
    className,
    children,
    ...rest
  },
  ref,
) {
  return (
    <div
      ref={ref}
      {...rest}
      className={cn(
        VARIANT_CLS[variant],
        RADIUS_CLS[radius],
        SHADOW_CLS[shadow],
        PADDING_CLS[padding],
        className,
      )}
    >
      {children}
    </div>
  );
});

/** Card section header — UPPERCASE caption + optional right-aligned action node. */
export function CardHeader({
  caption,
  action,
  className,
}: {
  caption: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex items-center justify-between mb-4', className)}>
      <p className="text-[10.5px] uppercase tracking-[0.18em] text-ink-500 font-semibold">
        {caption}
      </p>
      {action}
    </div>
  );
}
